/* eslint-disable camelcase */

import { CriticalEventContext } from './CriticalEventContext'
import { DangerousState, UserCriticalEventConfirmation } from '@/enum'
import { mdiAutorenew, mdiCheckCircle, mdiCloseCircle } from '@mdi/js'
import Vuetify from '../../plugins/vuetify'
import { UserSettings } from '../user/UserSettings'
import { criticalStatesColors, travelDateLocalized, valueFormatted } from '@/utils/travels'
import { CriticalDatasetEntry } from '../criticalDataset/CriticalDatasetEntry'

export const normalizeState = (state) => {
  const unifiedDangerousState = state.replace(/ /g, '_')
  return unifiedDangerousState.toLocaleLowerCase()
}

export const localizeState = (state) => {
  const unifiedDangerousState = normalizeState(state)

  const key = `$vuetify.map.criticalPopup.title.${unifiedDangerousState}`
  const result = Vuetify.framework.lang.t(key)
  return result !== key ? result : `${Vuetify.framework.lang.t(`$vuetify.map.criticalPopup.title.${DangerousState.UNKNOWN}`)}${unifiedDangerousState}`
}

export const stateColor = {
  [UserCriticalEventConfirmation.UNKNOWN]: 'black',
  [UserCriticalEventConfirmation.IGNORE]: 'gray',
  [UserCriticalEventConfirmation.APPROVE]: 'green darken-3',
  [UserCriticalEventConfirmation.REJECT]: 'red',
  [UserCriticalEventConfirmation.RETRAIN]: 'orange darken-1',
  [UserCriticalEventConfirmation.APPROVED_RETRAIN]: 'blue darken-2'
}

export const colorState = (state, retrainState) => {
  // TODO: add colors to criticalStatesColors and replace hardcode values in all suitable states
  if (state !== UserCriticalEventConfirmation.APPROVE || (state === UserCriticalEventConfirmation.APPROVE && retrainState === null)) {
    return stateColor[state]
  } else {
    return criticalStatesColors.retrainApproved
  }
}

export class CriticalEvent {
  constructor ({
    id_critical_event = -1, datetimestart = 0, datetime = 0, duration = 0, latitude = 0.0, longitude = 0.0, dangerousstate = '',
    recommendationname = '', recommendationdetail = '', context = {}, index = -1, settings = {}, device_info = {},
    state = UserCriticalEventConfirmation.UNKNOWN, timeeventstart = 0, timeeventend = 0, criticalparamvalue = '', tag = {}, is_demo = false,
    is_dataset = null, is_turning = null, is_camera_correct = null,
    is_look_down = null, is_wearing_mask = null, retrain_state = null,
    reject_reason = null, reject_comment = null, video_tag = {}, video_road_tag = {},
    dataset = {}, satellite_count = null, location_accuracy = null,
    is_fixed = false
  } = {}) {
    this.idCriticalEvent = id_critical_event
    this.datetimestart = new Date(datetimestart)
    this.datetime = new Date(datetime)
    this._duration = duration
    this.latitude = latitude
    this.longitude = longitude
    this._dangerousState = dangerousstate
    this.recommendationName = recommendationname
    this.recommendationDetail = recommendationdetail
    this.context = new CriticalEventContext(context)
    this.settings = new UserSettings(settings)
    this.deviceInfo = device_info
    this.state = state
    this.index = index
    this.timeEventStart = new Date(timeeventstart)
    this.timeEventEnd = new Date(timeeventend)
    this.criticalParamValue = criticalparamvalue
    this.tag = typeof tag === 'object' ? tag : JSON.parse(tag)
    this.isDemo = is_demo
    this.isDataset = is_dataset
    this.isTurning = is_turning
    this.isCameraCorrect = is_camera_correct
    this.isLookDown = is_look_down
    this.isWearingMask = is_wearing_mask
    this.retrainState = retrain_state
    this.rejectReason = reject_reason
    this.rejectComment = reject_comment
    this.videoTag = typeof video_tag === 'object' ? video_tag : JSON.parse(video_tag)
    this.videoRoadTag = typeof video_road_tag === 'object' ? video_road_tag : JSON.parse(video_road_tag)
    this.dataset = new CriticalDatasetEntry({ ...dataset })
    this.satelliteCount = satellite_count
    this.locationAccuracy = location_accuracy
    this.isFixed = is_fixed
  }

  get criticalParamValueObject () {
    let obj = {}
    try {
      obj = JSON.parse(this.criticalParamValue)
    } catch (e) {
      // old format - try to parse it
      const chuncks = this.criticalParamValue.split(';').map(chunck => chunck.trim())

      for (const chunck of chuncks) {
        const pair = chunck.split(':')
        if (pair.length === 2) {
          obj[pair[0]] = pair[1]
        } else {
          const perclos = 'perclos(avg):'
          pair[1] = pair[1].replace(perclos, '')
          obj[pair[0]] = pair[1]
          obj[perclos] = pair[2]
        }
      }
    }

    return obj
  }

  get point () {
    return [this.latitude, this.longitude]
  }

  get duration () {
    return (this._duration / 1000).toFixed(3)
  }

  get dangerousStateNormalized () {
    return normalizeState(this._dangerousState)
  }

  get formattedTimeEventStart () {
    return travelDateLocalized(this.timeEventStart)
  }

  get formattedTimeEventEnd () {
    return travelDateLocalized(this.timeEventEnd)
  }

  get dangerousStateLocalized () {
    return localizeState(this._dangerousState)
  }

  getLocalizedDangerousState (state) {
    return localizeState(state)
  }

  get getLocationAccuracyFormatted () {
    return valueFormatted(this.locationAccuracy)
  }

  getColorState () {
    return colorState(this.state, this.retrainState)
  }

  isStateConfirmed () {
    return [UserCriticalEventConfirmation.APPROVE, UserCriticalEventConfirmation.REJECT, UserCriticalEventConfirmation.RETRAIN].includes(this.state)
  }

  hasCriticalParamValue () {
    return Object.keys(this.criticalParamValueObject).length > 0
  }

  hasDeviceInfo () {
    return Object.keys(this.deviceInfo).length > 0
  }

  hasVideoTag () {
    return Object.keys(this.videoTag).length > 0
  }

  hasVideoRoadTag () {
    return Object.keys(this.videoRoadTag).length > 0
  }

  hasTag () {
    return Object.keys(this.tag).length > 0
  }

  get isRetrainingState () {
    return this.state === UserCriticalEventConfirmation.RETRAIN || (this.state === UserCriticalEventConfirmation.APPROVE && this.retrainState !== null)
  }

  getStateIcon (state = null) {
    if (!state) {
      state = this.state
    }

    if (state === UserCriticalEventConfirmation.APPROVE) {
      return mdiCheckCircle
    } else if (state === UserCriticalEventConfirmation.REJECT) {
      return mdiCloseCircle
    } else if (state === UserCriticalEventConfirmation.RETRAIN) {
      return mdiAutorenew
    } else {
      return ''
    }
  }

  getStateText (state = null) {
    if (!state) {
      state = this.state
    }

    return `$vuetify.map.criticalPopup.stateHelper.${state}`
  }

  get isDatabaseCritical () {
    return this.idCriticalEvent !== -1
  }
}
